import { CalendarClock, Landmark, MapPin, ShoppingBag, Volleyball } from 'lucide-react';
import moment from 'moment';
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom';
import { Paperclip2, SaveAdd, TicketDiscount } from 'iconsax-react';
import { downloadInvoice, paymentOrderId } from '../Utils/API';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import RazerPayCapture from './RazerPayCapture';



function BookingDetails() {
    const location = useLocation();
    const data = location.state;
    const [venueImage, setVenueImage] = React.useState(null);
    const [showRetryButton, setShowRetryButton] = React.useState(false);
    const [orderState, setOrderState] = React.useState({});
    const Userdetails = useSelector(state => state.user?.userDetails)
    

    const generateOrderId = async (amount, bookingType = "court", bookingId) => {
        const { status, order_id, currency } = await paymentOrderId({
            amount: Math.round(amount * 100),
            currency: 'INR',
            booking_type: bookingType,
            booking_id: bookingId
        });

        if (status === "created") {
            setOrderState({ order_id, currency, status, amount, Userdetails, booking_id: bookingId });
        } else {
            toast.error("Payment failed");
        }
    }

    const retryPayment = () => {
        if (data?.transaction_log?.length == 0) {
            generateOrderId(data?.partial_payment? data?.partial_total_price : data?.total_price, "court", data?.id)
        } else {
            setOrderState({
                order_id: data.transaction_log[0]?.order_id,
                currency: 'INR',
                status: 'created',
                amount: data.partial_payment ? data?.partial_total_price : data?.total_price,
                Userdetails: Userdetails,
                booking_id: data?.id
            });
        }
    }

    useEffect(() => {
        if(data){
            const actualStartDateTimeStr = `${data?.booking_date} ${data?.time_slot?.split(' - ')[0]}`
            const actualBookingTimeObject = moment(actualStartDateTimeStr, 'YYYY-MM-DD HH:mm A')
            const isBookingTimePassed = actualBookingTimeObject.isBefore(moment())
            const isThreeMinutesPassed = moment().diff(moment(data?.create_datetime).add(3, 'minutes'), 'minutes') < 3 ? false : true;
            const showButton = isThreeMinutesPassed ? false : data?.payment_status == 'Pending' && !isBookingTimePassed ;
            setShowRetryButton(showButton);
            const imageData = JSON.parse(data?.venue?.profile_image)
            if (imageData && imageData?.length > 0) {
                setVenueImage(imageData[0]?.media_url)
            }  
        }else {
            setVenueImage(null)
        }
    },[data])

    const refundSummary = React.useMemo(() => {
        if (!data?.cancelled && data?.payment_status !== 'Cancelled') {
            return null;
        }

        const paidAmount = Number(data?.amount_paid || 0);
        const convenienceFee = Number(data?.platform_fee ?? data?.partial_platform_fee ?? 0);
        const refundAmount = Math.max(paidAmount - convenienceFee, 0);

        return {
            refundAmount: refundAmount.toFixed(2),
            convenienceFee: convenienceFee.toFixed(2),
            amountPaid: paidAmount.toFixed(2),
        };
    }, [data]);

    const slotPriceDisplay = Number(data?.total_slot_price ?? data?.slot_price ?? data?.partial_total_slot_price ?? 0);
    const addonPriceDisplay = Number(data?.addon_price ?? data?.partial_addon_price ?? 0);
    const discountDisplay = Number(data?.discount_price ?? data?.partial_discount_price ?? 0);
    const gstDisplay = Number(data?.gst_amount ?? data?.partial_gst_amount ?? 0);
    const platformDisplay = Number(data?.platform_fee ?? data?.partial_platform_fee ?? 0);
    const needToPay = data?.payment_status !== "Pending"
        ? Number(Number(data?.total_price || 0) - Number(data?.partial_total_price || 0)).toFixed(2)
        : data?.total_price;

    return (
        <div className="flex flex-col justify-center items-center mt-4">
            {['Partially Paid', 'Paid'].includes(data?.payment_status) && <div className='flex w-full max-w-4xl justify-end mb-5 px-4 sm:px-0'>
                <button
                    onClick={() => downloadInvoice(data?.id, `${data?.booking_date}_${data?.venue?.name}`)}
                    className="mt-4 bg-red-500 text-white py-2 px-4 rounded-full hover:bg-red-600 transition-all duration-200 exclude-from-pdf"
                >
                    Download Invoice
                </button>
            </div>}    
            <div className="w-full max-w-4xl bg-white shadow-xl rounded-lg p-6 space-y-6 hover:shadow-2xl transition-shadow duration-300 ease-in-out booking-details">
                <div className="border-b-2 pb-4 space-y-2 border-gray-200">
                    <div className='flex flex-row justify-between items-center border-b border-gray-300 border-dashed pb-2 mb-4'>
                        <p className="capitalize font-semibold text-red-500 text-lg">{data?.booking_id}</p>
                        <div className="flex justify-center items-center w-auto h-full">
                            <p
                                className={`font-semibold text-sm px-2 py-1 rounded-md flex justify-center items-center ${data?.payment_status === 'Paid'
                                    ? 'bg-green-200 text-green-600'
                                    : data?.payment_status === 'Pending'
                                        ? 'bg-yellow-200 text-yellow-600'
                                        : data?.payment_status === 'Partially Paid' ? 'bg-violet-100 text-violet-600' : 'bg-red-200 text-red-600'
                                    }`}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                {data?.payment_status}
                            </p>
                        </div>
                    </div>
                    <div className='flex flex-col sm:flex-row gap-4'>
                        <div>
                            <img className='h-24 w-40 rounded-md shadow bg-gray-200' src={venueImage} alt="Venue Image" />
                        </div>
                        <div className='flex flex-col gap-1.5'>
                            <p className="font-bold text-2xl text-gray-800">{data?.venue_name}</p>
                            <div className='flex items-start gap-2'>
                                <MapPin strokeWidth={1.25} size={20} color='red' />
                                <p className="text-sm text-gray-500">{data?.venue?.address}</p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <CalendarClock strokeWidth={1.25} size={20} color='red' />
                                <p className="text-sm text-gray-500">
                                    {data?.time_slot} • {moment(data?.booking_date).format("MMMM DD, YYYY")}
                                </p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <Volleyball strokeWidth={1.25} size={20} color='red' />
                                <p className="text-sm text-gray-500">{data?.sport_name}</p>
                            </div>

                            {/* Payment Status Section */}
                            {/* <div className="flex items-center gap-2">
                                <p className="font-semibold text-gray-800">Payment Status:</p>
                                
                            </div> */}
                        </div>
                    </div>
                </div>
                <div className="pb-4">
                    <p className="font-semibold text-lg text-gray-800">User Details</p>
                    <div className="mt-4 text-sm flex justify-between items-center border-b pb-2 border-gray-100">
                        <p className="font-semibold text-gray-800">Full Name</p>
                        <p className="text-black font-semibold">{`${data?.user?.first_name || ''} ${data?.user?.last_name || ''}`.trim() || 'N/A'}</p>
                    </div>
                </div>
                <div className="pb-4">
                    <p className="font-semibold text-lg text-gray-800">Payment Details</p>
                    <div className="mt-4 space-y-3 text-sm">
                        <div className="flex justify-between items-center border-b pb-2 border-gray-100 hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <ShoppingBag size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Slot Price</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {slotPriceDisplay.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2 border-gray-100 hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <SaveAdd size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Addons Price</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {addonPriceDisplay.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2 border-gray-100 hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <TicketDiscount size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Discount Amount</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {discountDisplay.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2 border-gray-100 hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <Landmark size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">GST</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {gstDisplay.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2 border-gray-300 border-dashed hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <Paperclip2 size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Convenience Fee</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {platformDisplay.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center hover:bg-gray-50 transition-all duration-200">
                            <p className="font-semibold text-gray-800">Sub Total</p>
                            <p className="font-semibold text-gray-800">₹ {data?.total_price}</p>
                        </div>
                        <div className="flex justify-between items-center hover:bg-gray-50 transition-all duration-200">
                            <p className="font-semibold text-gray-800">Amount Paid</p>
                            <p className="font-semibold text-gray-800">₹ {data?.amount_paid}</p>
                        </div>
                        <div className="flex justify-between items-center py-2 border-t border-dashed border-slate-200 hover:bg-gray-50 transition-all duration-200">
                            <p className="font-semibold text-gray-800">Remaining Amount</p>
                            <p className="font-semibold text-gray-800">₹ {(Number(data?.total_price - data?.amount_paid)).toFixed(2) }</p>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center text-red-500 border-t border-dashed pt-4 border-gray-200">
                    <p className="font-semibold text-xl">Grand Total</p>
                    <p className="font-semibold text-xl text-red-600">₹ {data?.total_price}</p>
                </div>

                {refundSummary && data?.payment_status === 'Cancelled' && (
                    <div className="pt-6 border-t border-gray-200">
                        <p className="font-semibold text-lg text-gray-800 mb-3">Refund Details</p>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Amount Paid</p>
                                <p className="font-semibold">₹ {refundSummary.amountPaid}</p>
                            </div>
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Convenience Fee</p>
                                <p className="text-red-500">- ₹ {refundSummary.convenienceFee}</p>
                            </div>
                            <div className="border-t border-dashed" />
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Refund Amount</p>
                                <p className="font-semibold text-green-600">₹ {refundSummary.refundAmount}</p>
                            </div>
                        </div>
                    </div>
                )}

                {Boolean(data?.partial_payment) && (
                    <div className="pt-6 border-t border-gray-200">
                        <p className="font-semibold text-lg text-gray-800 mb-3">Partial Payment Summary</p>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Amount Paid</p>
                                <p className="font-semibold">₹ {Number(data?.amount_paid || 0).toFixed(2)}</p>
                            </div>
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Need To Pay</p>
                                <p className="font-semibold">₹ {Number(needToPay || 0).toFixed(2)}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showRetryButton && <div className='flex w-full max-w-4xl flex-row justify-end items-center'>
                <button
                    disabled={!showRetryButton}
                    onClick={retryPayment}
                    className="mt-4 bg-red-500 text-white py-2 px-4 rounded-full hover:bg-red-600 transition-all duration-200 exclude-from-pdf"
                >
                    Retry Payment
                </button>
                <RazerPayCapture orderState={orderState} />
            </div>}
        </div>

    )
}

export default BookingDetails
